import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Pencil, 
  Eye,
  Trash2, 
  Loader2, 
  Plus, 
  FileText,
  Receipt,
  DollarSign,
  CreditCard,
  Building
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHeader, TableHead, TableRow } from '@/components/ui/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collection, query, where, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/components/AuthProvider';
import { getTemplateGeneratedInvoices } from '@/lib/api';
import { showNotification } from '@/lib/generalNotifications';
import { PageTitle } from '@/components/ui/PageTitle';
import { formatCurrency } from '@/lib/utils';
import { getFrontendBaseUrl } from '@/lib/environment';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { RecurringFrequencyDetails } from '@/types/recurring';
import type { PaymentMethod } from '@/types/firebase';


// Format date helper
function formatLocalDate(dateStr: string) {
  if (!dateStr) return '';
  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString();
  } catch {
    // If it's already a Date object or timestamp
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return '';
    }
  }
}

// Template interface with enhanced tracking fields
interface InvoiceTemplate {
  id: string;
  baseInvoiceId?: string;
  companyId: string;
  customerId?: string;
  customerName: string;
  customerEmail: string;
  customerDetails?: {
    id?: string;
    name?: string;
    email?: string;
    address?: string;
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  invoiceNumber: string;
  emailTemplate: {
    subject: string;
    body: string;
  };
  lineItems: any[];
  invoiceDetails: {
    subtotal: number;
    taxRate: number;
    taxAmount: number;
    platformFee: number;
    total: number;
    notes?: string;
  };
  nextGenerationDate?: Date | null;
  recurringDetails: {
    frequency: 'weekly' | 'monthly' | 'quarterly' | 'annual';
    endDate?: Date | null;
    remainingOccurrences?: number | null;
    isActive: boolean;
    frequencyDetails?: RecurringFrequencyDetails;
    enableACH?: boolean;
  };
  status: string;
  hasBeenActivated?: boolean;
  createdAt: any;
  updatedAt: any;
  // New fields for payment method storage and invoice tracking
  storedPaymentMethod?: {
    paymentMethodId: string;
    nmiCustomerId: string;
    type: 'credit_card' | 'ach';
    last4: string;
    cardType?: string;
    expiryMonth?: string;
    expiryYear?: string;
    accountType?: string;
    routingNumberLast4?: string;
    nickName?: string;
    capturedAt: Date;
  };
  generatedInvoices?: {
    invoiceId: string;
    invoiceNumber: string;
    generatedAt: Date;
    dueDate: string;
    amount: number;
    status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'pending';
    paymentDate?: Date;
    transactionId?: string;
  }[];
  totalGenerated?: {
    count: number;
    totalAmount: number;
    paidCount: number;
    paidAmount: number;
    lastUpdated: Date;
  };
}

interface InvoiceTemplatesTableProps {
  companySettings: any;
}

export function InvoiceTemplatesTable({ companySettings }: InvoiceTemplatesTableProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<InvoiceTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAnalyticsDialogOpen, setIsAnalyticsDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<InvoiceTemplate | null>(null);
  const [templateAnalytics, setTemplateAnalytics] = useState<any>(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [customerPaymentMethods, setCustomerPaymentMethods] = useState<PaymentMethod[]>([]);

  // Fetch templates
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['invoiceTemplates'],
    queryFn: async () => {
      if (!user?.companyId) return [];
      
      const templatesRef = collection(db, 'invoiceTemplates');
      const q = query(templatesRef, where('companyId', '==', user.companyId));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as InvoiceTemplate[];
    },
    enabled: !!user?.companyId
  });

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateId: string) => {
      await deleteDoc(doc(db, 'invoiceTemplates', templateId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoiceTemplates'] });
      showNotification({
        title: 'Success',
        message: 'Template deleted successfully',
        type: 'success'
      });
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    },
    onError: (error) => {
      console.error('Error deleting template:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to delete template',
        type: 'error'
      });
      setIsDeleting(false);
    }
  });

  // Toggle active status mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive, currentHasBeenActivated }: { id: string; isActive: boolean; currentHasBeenActivated?: boolean }) => {
      const templateRef = doc(db, 'invoiceTemplates', id);
      const updateData: any = {
        'recurringDetails.isActive': isActive,
        updatedAt: new Date()
      };
      if (isActive && !currentHasBeenActivated) {
        updateData.hasBeenActivated = true;
      }
      await updateDoc(templateRef, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoiceTemplates'] });
      showNotification({
        title: 'Success',
        message: 'Template status updated',
        type: 'success'
      });
    },
    onError: (error) => {
      console.error('Error updating template status:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to update template status',
        type: 'error'
      });
    }
  });

  // Handle edit template
  const handleEditTemplate = (template: InvoiceTemplate) => {
    // Navigate to edit-template route without the step parameter to start at step 1
    navigate(`/app/invoices/edit-template/${template.id}`);
  };

  // Handle preview template
  const handlePreviewTemplate = (template: InvoiceTemplate) => {
    // Allow direct preview of the template regardless of baseInvoiceId
    navigate(`/app/invoices/preview?id=${template.id}`);
  };

  // Handle delete click
  const handleDeleteClick = (template: InvoiceTemplate) => {
    setTemplateToDelete(template);
    setIsDeleteDialogOpen(true);
  };

  // Confirm delete template
  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;
    
    setIsDeleting(true);
    await deleteTemplateMutation.mutateAsync(templateToDelete.id);
  };

  // Create template
  const handleCreateTemplate = () => {
    const baseUrl = getFrontendBaseUrl();
    window.open(`${baseUrl}/app/invoices/new?type=recurring`, '_blank');
  };

const shouldApplyPlatformFee = (template: InvoiceTemplate) => {
  if (!companySettings?.platformFee) return false;
  const templateFee = template.invoiceDetails?.platformFee ?? 0;
  return companySettings.platformFee > 0 && templateFee > 0;
};

  // Generate new invoice from template - currently unused
  /*
  const generateInvoice = async (template: InvoiceTemplate) => {
    try {
      // Here you would typically call an API function to generate a new invoice
      // For now, we'll just show a notification
      showNotification({
        title: 'Generate Invoice',
        message: 'This will create a new invoice from the template. Feature coming soon!',
        type: 'warning'
      });

      // When implemented, this function would:
      // 1. Create a new invoice document based on the template
      // 2. Set appropriate status, dates, etc.
      // 3. Return the new invoice ID
      // 4. Navigate to the new invoice
    } catch (error) {
      console.error('Error generating invoice:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to generate invoice from template',
        type: 'error'
      });
    }
  };
  */

  // View template analytics
  const handleViewAnalytics = async (template: InvoiceTemplate) => {
    setSelectedTemplate(template);
    setIsLoadingAnalytics(true);
    setIsAnalyticsDialogOpen(true);
    
    try {
      // Fetch template analytics
      const analytics = await getTemplateGeneratedInvoices(template.id);
      setTemplateAnalytics(analytics);
      
      // Fetch customer payment methods
      if (template.customerId || template.customerDetails?.id) {
        const customerId = template.customerId || template.customerDetails?.id;
        const paymentMethodsRef = collection(db, 'paymentMethods');
        const q = query(
          paymentMethodsRef,
          where('customerId', '==', customerId),
          where('companyId', '==', template.companyId)
        );
        
        const snapshot = await getDocs(q);
        const methods = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as PaymentMethod));
        
        setCustomerPaymentMethods(methods);
      }
    } catch (error) {
      console.error('Error fetching template analytics:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to load template analytics',
        type: 'error'
      });
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <PageTitle title="Recurring Invoice Templates" />
        <Button 
          onClick={handleCreateTemplate}
          className="bg-primary hover:bg-primary/90 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-[8%]">Created Date</TableHead>
                <TableHead className="w-[12%]">Template Number</TableHead>
                <TableHead className="w-[15%]">Customer</TableHead>
                <TableHead className="w-[10%] text-right">Amount</TableHead>
                <TableHead className="w-[8%] text-center">Platform Fee</TableHead>
                <TableHead className="w-[10%]">Frequency</TableHead>
                <TableHead className="w-[12%] text-center">Payment Method</TableHead>
                <TableHead className="w-[8%] text-center">Status</TableHead>
                <TableHead className="w-[17%] text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="h-36">
                    <div className="flex flex-col items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-gray-400 mb-2" />
                      <p className="text-gray-500">Loading templates...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="h-36">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <FileText className="h-10 w-10 text-gray-300" />
                      <p className="text-gray-500">No recurring templates found</p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleCreateTemplate}
                      >
                        Create your first template
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template) => {
                  const baseTotalAmount = (template.invoiceDetails?.subtotal || 0) + (template.invoiceDetails?.taxAmount || 0);
                  const platformFeeAmount = shouldApplyPlatformFee(template) ? 
                    baseTotalAmount * ((companySettings?.platformFee || 0) / 100) : 0;
                  
                  return (
                    <TableRow key={template.id} className="hover:bg-gray-50/50 transition-colors">
                      <TableCell className="py-4">
                        {template.createdAt ? formatLocalDate(template.createdAt.toDate?.() || template.createdAt) : ''}
                      </TableCell>
                      <TableCell className="py-4 font-medium">
                        {template.invoiceNumber}
                      </TableCell>
                      <TableCell className="py-4">
                        {template.customerName}
                        <div className="text-xs text-gray-500">{template.customerEmail}</div>
                      </TableCell>
                      <TableCell className="py-4 text-right">
                        {formatCurrency(baseTotalAmount)}
                      </TableCell>
                      <TableCell className="py-4 text-center">
                        {platformFeeAmount > 0 ? formatCurrency(platformFeeAmount) : <span className="text-xs text-gray-500">N/A</span>}
                      </TableCell>
                      <TableCell className="py-4 capitalize">
                        {template.recurringDetails?.frequency || 'Monthly'}
                      </TableCell>
                      <TableCell className="py-4 text-center">
                        {template.storedPaymentMethod ? (
                          <div className="flex items-center justify-center gap-1">
                            <CreditCard className="h-4 w-4 text-green-600" />
                            <span className="text-xs text-green-700">
                              {template.storedPaymentMethod.type === 'credit_card' 
                                ? `${template.storedPaymentMethod.cardType || 'Card'} ••••${template.storedPaymentMethod.last4}`
                                : `${template.storedPaymentMethod.accountType || 'ACH'} ••••${template.storedPaymentMethod.last4}`
                              }
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">Not captured</span>
                        )}
                      </TableCell>
                      <TableCell className="py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Badge className={template.recurringDetails?.isActive ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20' : 'bg-gray-100 text-gray-500'}>
                            {template.recurringDetails?.isActive ? 'Active' : 'Paused'}
                          </Badge>
                          <Switch
                            checked={template.recurringDetails.isActive}
                            onCheckedChange={(newIsActive) => {
                              if (newIsActive && !template.hasBeenActivated) {
                                navigate(`/app/invoices/preview?id=${template.id}`);
                              } else {
                                toggleActiveMutation.mutate({ 
                                  id: template.id, 
                                  isActive: newIsActive,
                                  currentHasBeenActivated: template.hasBeenActivated 
                                });
                              }
                            }}
                            disabled={toggleActiveMutation.isPending}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Template Analytics Button */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" onClick={() => handleViewAnalytics(template)}>
                                  <Receipt className="h-4 w-4 text-purple-600" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>View Generated Invoices & Analytics</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          {/* Conditional Edit/View Button */}
                          {template.hasBeenActivated ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="sm" onClick={() => handlePreviewTemplate(template)}>
                                    <Eye className="h-4 w-4 text-blue-600" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>View Template</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="sm" onClick={() => handleEditTemplate(template)}>
                                    <Pencil className="h-4 w-4 text-blue-600" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Edit Template</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}

                          {/* Existing Delete Button */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(template)} className="text-red-600 hover:text-red-700">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Delete Template</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this recurring invoice template? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm mb-4">
            <p>
              <strong>Warning:</strong> This will delete the template permanently, but it will not affect any invoices that have already been generated from this template.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={confirmDeleteTemplate}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Template'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Analytics Dialog */}
      <Dialog open={isAnalyticsDialogOpen} onOpenChange={setIsAnalyticsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-purple-600" />
              Template Analytics: {selectedTemplate?.invoiceNumber}
            </DialogTitle>
            <DialogDescription>
              View all invoices generated from this template and revenue analytics
            </DialogDescription>
          </DialogHeader>
          
          {isLoadingAnalytics ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
              <span className="text-gray-500">Loading analytics...</span>
            </div>
          ) : templateAnalytics ? (
            <div className="space-y-6">
              {/* Analytics Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-600" />
                    <div>
                      <p className="text-sm text-gray-600">Total Generated</p>
                      <p className="text-xl font-semibold">{templateAnalytics.totals?.count || 0}</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-sm text-gray-600">Total Revenue</p>
                      <p className="text-xl font-semibold">{formatCurrency(templateAnalytics.totals?.totalAmount || 0)}</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4">
                  <div className="flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-purple-600" />
                    <div>
                      <p className="text-sm text-gray-600">Paid Invoices</p>
                      <p className="text-xl font-semibold">{templateAnalytics.totals?.paidCount || 0}</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-emerald-600" />
                    <div>
                      <p className="text-sm text-gray-600">Paid Amount</p>
                      <p className="text-xl font-semibold">{formatCurrency(templateAnalytics.totals?.paidAmount || 0)}</p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Payment Method Info */}
              {selectedTemplate?.storedPaymentMethod && (
                <Card className="p-4 bg-green-50 border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="h-4 w-4 text-green-600" />
                    <h3 className="font-medium text-green-800">Stored Payment Method</h3>
                  </div>
                  <p className="text-sm text-green-700">
                    {selectedTemplate.storedPaymentMethod.type === 'credit_card' 
                      ? `${selectedTemplate.storedPaymentMethod.cardType || 'Card'} ending in ${selectedTemplate.storedPaymentMethod.last4}`
                      : `${selectedTemplate.storedPaymentMethod.accountType || 'ACH'} ending in ${selectedTemplate.storedPaymentMethod.last4}`
                    }
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    Captured on {new Date(selectedTemplate.storedPaymentMethod.capturedAt).toLocaleDateString()}
                  </p>
                </Card>
              )}

              {/* Customer Payment Methods */}
              {customerPaymentMethods.length > 0 && (
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard className="h-4 w-4 text-blue-600" />
                    <h3 className="font-medium">Customer Payment Methods on File</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {customerPaymentMethods.map((method) => (
                      <div key={method.id} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
                        {method.type === 'credit_card' ? (
                          <CreditCard className="h-4 w-4 text-gray-600" />
                        ) : (
                          <Building className="h-4 w-4 text-gray-600" />
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-medium">
                            {method.type === 'credit_card' 
                              ? `${method.cardType || 'Card'} •••• ${method.last4}`
                              : `${method.accountType || 'ACH'} •••• ${method.last4}`
                            }
                          </p>
                          {method.nickName && (
                            <p className="text-xs text-gray-500">{method.nickName}</p>
                          )}
                        </div>
                        {method.isDefault && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            Default
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Generated Invoices Table */}
              <div>
                <h3 className="text-lg font-medium mb-3">Generated Invoices</h3>
                {templateAnalytics.invoices && templateAnalytics.invoices.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Invoice #</TableHead>
                          <TableHead>Generated Date</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                          <TableHead className="text-center">Payment Date</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {templateAnalytics.invoices.map((invoice: any) => (
                          <TableRow key={invoice.invoiceId}>
                            <TableCell className="font-medium">{invoice.invoiceNumber}</TableCell>
                            <TableCell>
                              {invoice.generatedAt ? (
                                invoice.generatedAt.toDate ? 
                                  new Date(invoice.generatedAt.toDate()).toLocaleDateString() : 
                                  invoice.generatedAt instanceof Date ?
                                    invoice.generatedAt.toLocaleDateString() :
                                    new Date(invoice.generatedAt).toLocaleDateString()
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {invoice.dueDate ? (
                                // Handle MM/DD/YYYY format
                                typeof invoice.dueDate === 'string' && invoice.dueDate.match(/^\d{2}\/\d{2}\/\d{4}$/) ?
                                  invoice.dueDate :
                                  // Handle YYYY-MM-DD format
                                  typeof invoice.dueDate === 'string' && invoice.dueDate.match(/^\d{4}-\d{2}-\d{2}$/) ?
                                    new Date(invoice.dueDate + 'T12:00:00').toLocaleDateString() :
                                    new Date(invoice.dueDate).toLocaleDateString()
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-right">{formatCurrency(invoice.amount)}</TableCell>
                            <TableCell className="text-center">
                              <Badge className={
                                invoice.status === 'paid' ? 'bg-green-50 text-green-700 ring-1 ring-green-600/20' :
                                invoice.status === 'overdue' ? 'bg-red-50 text-red-700 ring-1 ring-red-600/20' :
                                invoice.status === 'sent' ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20' :
                                'bg-gray-100 text-gray-700'
                              }>
                                {invoice.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {invoice.paymentDate ? new Date(invoice.paymentDate).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => navigate(`/app/invoices/preview?id=${invoice.invoiceId}`)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    <p>No invoices have been generated from this template yet.</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Failed to load analytics data.</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAnalyticsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
} 
